import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchListing, requestListingContact, saveListing } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ArrowLeft, MapPin, Users, Calendar, FileText, ShieldCheck, Bookmark, Mail, Volume2, VolumeX, Play } from "lucide-react";
import ShareButton from "../components/ShareButton";

export default function ListingDetailPage() {
  const { id } = useParams();
  const { format } = useCurrency();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  const [form, setForm] = useState({ buyer_name: "", buyer_email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    fetchListing(id).then(setListing).catch(() => toast.error("Listing not found"));
  }, [id]);

  useEffect(() => {
    if (user) setForm(f => ({ ...f, buyer_name: user.full_name || "", buyer_email: user.email }));
  }, [user]);

  if (!listing) return <div className="p-20 text-center text-slate-400">Loading...</div>;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await requestListingContact(id, { listing_id: id, ...form });
      toast.success("Request sent. The owner will reply via DealVest.");
      setForm({ buyer_name: user?.full_name || "", buyer_email: user?.email || "", message: "" });
    } catch { toast.error("Failed to send request."); }
    setSubmitting(false);
  };

  const save = async () => {
    const email = user?.email || localStorage.getItem("dv_user_email") || prompt("Enter your email to save:");
    if (!email) return;
    localStorage.setItem("dv_user_email", email);
    try { await saveListing({ user_email: email, listing_id: id }); toast.success("Saved to your dashboard."); }
    catch { toast.error("Failed to save."); }
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
    else { videoRef.current.pause(); setPlaying(false); }
  };

  return (
    <div data-testid="listing-detail-page" className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
      <Link to="/marketplace" className="inline-flex items-center gap-2 text-slate-500 hover:text-blue-600 mb-6 text-sm font-medium">
        <ArrowLeft size={14} /> Back to Marketplace
      </Link>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {listing.video_url ? (
            <div className="relative aspect-video rounded-2xl overflow-hidden mb-6 bg-slate-900">
              <video ref={videoRef} src={listing.video_url} poster={listing.image_url} muted={muted} loop playsInline className="w-full h-full object-cover" onClick={togglePlay} data-testid="listing-video" />
              <button onClick={togglePlay} data-testid="play-video-btn" className={`absolute inset-0 flex items-center justify-center transition-opacity ${playing ? "opacity-0 hover:opacity-100" : "opacity-100"}`}>
                <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center"><Play size={26} className="text-slate-900 ml-1" fill="currentColor" /></div>
              </button>
              <button onClick={() => setMuted(!muted)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/40 backdrop-blur text-white flex items-center justify-center" aria-label="Toggle sound">
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <span className="absolute top-4 left-4 dv-pill bg-blue-600 text-white">Pitch Video</span>
            </div>
          ) : (
            <div className="aspect-[16/9] rounded-2xl overflow-hidden mb-6 bg-slate-100">
              <img src={listing.image_url} alt={listing.business_name} className="w-full h-full object-cover" />
            </div>
          )}

          <div className="flex flex-wrap gap-2 mb-3">
            <span className="dv-pill">{listing.industry}</span>
            {listing.verified && <span className="dv-pill bg-emerald-50 text-emerald-700"><ShieldCheck size={12} /> Verified</span>}
            {listing.featured && <span className="dv-pill bg-amber-50 text-amber-700">Featured</span>}
          </div>

          <h1 className="dv-display text-3xl md:text-4xl mb-3">{listing.business_name}</h1>
          <div className="flex items-center justify-between gap-3 mb-8 flex-wrap">
            <div className="flex items-center gap-2 text-slate-500">
              <MapPin size={15} /> {listing.city ? `${listing.city}, ` : ""}{listing.country}
            </div>
            <ShareButton url={`/listing/${listing.id}`} title={`${listing.business_name} — ${listing.industry}`} text={`${listing.country} · ${listing.revenue_range} revenue`} variant="secondary" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
            {[
              { label: "Revenue", value: listing.revenue_range },
              { label: "Profit", value: listing.profit_range },
              { label: "Employees", value: listing.employees, icon: Users },
              { label: "Years", value: `${listing.years_operating} yrs`, icon: Calendar },
            ].map((s, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">{s.label}</div>
                <div className="font-bold text-slate-900 text-lg">{s.value}</div>
              </div>
            ))}
          </div>

          <section className="mb-8">
            <h2 className="dv-display text-2xl mb-3">Overview</h2>
            <p className="text-slate-700 text-base leading-relaxed">{listing.description}</p>
          </section>

          <section className="mb-8">
            <h2 className="dv-display text-2xl mb-3">Growth Opportunities</h2>
            <p className="text-slate-700 text-base leading-relaxed">{listing.growth_opportunities}</p>
          </section>

          <section className="mb-8">
            <h2 className="dv-display text-2xl mb-3">Documents</h2>
            <div className="space-y-2">
              {(listing.documents_available || []).map((d, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
                  <FileText size={18} className="text-blue-600" />
                  <span className="font-medium text-slate-900">{d}</span>
                  <span className="ml-auto text-xs text-slate-400 font-medium">NDA Required</span>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sticky top-24 dv-shadow-soft">
            <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-1">{listing.listing_type === "investment" ? "Seeking" : "Asking Price"}</div>
            <div className="text-3xl font-bold text-slate-900 mb-1">{format(listing.listing_type === "investment" ? listing.investment_requirement : listing.asking_price, { compact: false })}</div>
            <div className="text-slate-400 text-xs mb-6">USD equivalent · current rate</div>

            <button onClick={save} className="dv-btn dv-btn-secondary w-full mb-3" data-testid="save-listing-btn"><Bookmark size={15} /> Save Opportunity</button>

            <div className="border-t border-slate-100 pt-5 mt-5">
              <h3 className="font-bold text-lg mb-3">Request Information</h3>
              <form onSubmit={submit} className="space-y-3" data-testid="contact-listing-form">
                <input className="dv-input" placeholder="Your name" required value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} data-testid="contact-name" />
                <input type="email" className="dv-input" placeholder="Your email" required value={form.buyer_email} onChange={e => setForm({ ...form, buyer_email: e.target.value })} data-testid="contact-email" />
                <textarea className="dv-input is-textarea" placeholder="Brief message..." rows="3" required value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
                <button type="submit" disabled={submitting} className="dv-btn dv-btn-primary w-full" data-testid="contact-submit"><Mail size={15} /> {submitting ? "Sending..." : "Send Request"}</button>
              </form>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
